/* eslint-disable prettier/prettier, curly */
/**
 * Trash View - Shows deleted messages with restore option
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Text,
  Spinner,
  Checkbox,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Badge,
} from '@fluentui/react-components';
import {
  DeleteRegular,
  MailRegular,
  AttachRegular,
  MoreHorizontalRegular,
  ArrowUndoRegular,
} from '@fluentui/react-icons';
import { ApiClient, newToast } from '../../../../configurations/index.config';
import { getErrorMessage } from '../../../../types';
import { formatRelativeDate, stripHtmlTags } from '../../../../utils/memoUtils';
import { TablePagination } from '../../../../components/common';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  warning: {
    padding: '12px 16px',
    backgroundColor: '#fff4ce',
    border: '1px solid #f7e06e',
    borderRadius: '4px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  messageList: {
    flex: 1,
    overflowY: 'auto',
    border: '1px solid #e1dfdd',
    borderRadius: '4px',
  },
  messageItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #e1dfdd',
    cursor: 'pointer',
    backgroundColor: '#fafafa',
    transition: 'background-color 0.1s ease',
    ':hover': {
      backgroundColor: '#f3f2f1',
    },
  },
  checkbox: {
    marginRight: '12px',
  },
  messageIcon: {
    marginRight: '12px',
    fontSize: '20px',
    color: '#d13438',
  },
  messageContent: {
    flex: 1,
    minWidth: 0,
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  senderName: {
    fontWeight: '600',
    color: '#323130',
  },
  subject: {
    fontSize: '14px',
    color: '#323130',
    marginBottom: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  snippet: {
    fontSize: '13px',
    color: '#605e5c',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  messageTime: {
    fontSize: '13px',
    color: '#605e5c',
    marginLeft: '12px',
    whiteSpace: 'nowrap',
  },
  actionButton: {
    minWidth: 'auto',
    marginLeft: '12px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    color: '#605e5c',
    minHeight: '300px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
  bulkActions: {
    padding: '8px 16px',
    backgroundColor: '#f3f2f1',
    borderBottom: '1px solid #e1dfdd',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
});

interface Message {
  recipient_id: number;
  message_id: number;
  subject: string;
  snippet: string;
  sender_name: string;
  sender_alias: string;
  sent_at: string;
  deleted_at: string;
  days_in_trash: number;
  has_attachments: boolean;
}

interface ContextType {
  stats: any;
  mailbox: any;
  refreshStats: () => void;
}

export const TrashFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { refreshStats } = useOutletContext<ContextType>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    loadTrash();
  }, [currentPage, pageSize]);

  const loadTrash = async () => {
    try {
      setLoading(true);
      const response = await ApiClient.get('/memo/trash', {
        params: {
          page: currentPage,
          limit: pageSize,
        },
      });

      if (response.data.status === 'success') {
        setMessages(response.data.data.items || []);
        setTotalItems(response.data.data.pagination?.total || 0);
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleMessageClick = (messageId: number) => {
    navigate(`/account/memo/message/${messageId}`);
  };

  const handleSelectMessage = (messageId: number, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(messageId);
    } else {
      newSelected.delete(messageId);
    }
    setSelectedIds(newSelected);
  };

  const handleRestore = async () => {
    if (selectedIds.size === 0) {
      return;
    }

    try {
      const response = await ApiClient.post('/memo/restore', {
        message_ids: Array.from(selectedIds),
      });

      if (response.data.status === 'success') {
        newToast('success', `Restored ${selectedIds.size} message(s)`);
        loadTrash();
        refreshStats();
        setSelectedIds(new Set());
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handlePermanentDelete = async (messageId: number) => {
    if (!window.confirm('Permanently delete this message? This cannot be undone.')) {
      return;
    }

    try {
      const response = await ApiClient.delete(`/memo/permanent-delete/${messageId}`);

      if (response.data.status === 'success') {
        newToast('success', 'Message permanently deleted');
        loadTrash();
        refreshStats();
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading trash..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.title}>Trash</Text>
        <Text size={300} style={{ color: '#605e5c' }}>
          {totalItems} message{totalItems !== 1 ? 's' : ''}
        </Text>
      </div>

      {messages.length > 0 && (
        <div className={styles.warning}>
          <Text size={300}>
            Messages in trash for more than 90 days will be permanently deleted.
          </Text>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className={styles.bulkActions}>
          <Text size={300} weight="semibold">
            {selectedIds.size} selected
          </Text>
          <Button size="small" icon={<ArrowUndoRegular />} onClick={handleRestore}>
            Restore
          </Button>
        </div>
      )}

      <div className={styles.messageList}>
        {messages.length === 0 ? (
          <div className={styles.emptyState}>
            <DeleteRegular style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
            <Text size={400}>Trash is empty</Text>
          </div>
        ) : (
          messages.map(message => (
            <div key={message.message_id} className={styles.messageItem}>
              <Checkbox
                className={styles.checkbox}
                checked={selectedIds.has(message.message_id)}
                onChange={(e, data) =>
                  handleSelectMessage(message.message_id, data.checked === true)
                }
              />

              <div className={styles.messageIcon}>
                <MailRegular />
              </div>

              <div
                className={styles.messageContent}
                onClick={() => handleMessageClick(message.message_id)}
              >
                <div className={styles.messageHeader}>
                  <Text className={styles.senderName}>{message.sender_name}</Text>
                  {message.has_attachments && (
                    <AttachRegular style={{ fontSize: '16px', color: '#605e5c' }} />
                  )}
                  {message.days_in_trash >= 80 && (
                    <Badge color="danger" size="small">
                      Deletes in {90 - message.days_in_trash} days
                    </Badge>
                  )}
                </div>
                <Text className={styles.subject}>{message.subject}</Text>
                <Text className={styles.snippet}>{stripHtmlTags(message.snippet)}</Text>
              </div>

              <Text className={styles.messageTime}>
                Deleted {formatRelativeDate(message.deleted_at)}
              </Text>

              <Menu>
                <MenuTrigger>
                  <Button
                    appearance="subtle"
                    icon={<MoreHorizontalRegular />}
                    className={styles.actionButton}
                    onClick={e => e.stopPropagation()}
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem onClick={() => handleMessageClick(message.message_id)}>Open</MenuItem>
                    <MenuItem icon={<ArrowUndoRegular />} onClick={handleRestore}>
                      Restore
                    </MenuItem>
                    <MenuItem
                      icon={<DeleteRegular />}
                      onClick={() => handlePermanentDelete(message.message_id)}
                    >
                      Delete permanently
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ marginTop: '16px' }}>
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={pageSize}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={() => {}}
          />
        </div>
      )}
    </div>
  );
};

export default TrashFluentUI;
